import React, { useCallback, useEffect, useState } from 'react';
import { api } from '../../Api';
import { CartaoOcorrencia } from './index';

/** Quantas ocorrências visíveis aos pais ainda estão sem "Ciente". */
export function useOcorrenciasPendentes(id_student, recarregar = 0) {
    const [pend, setPend] = useState({ total: 0, graves: 0, elogios: 0 });
    useEffect(() => {
        let ativo = true;
        if (!id_student) return undefined;
        api.post('/occurrence/pending', { id_student })
            .then((r) => { if (ativo) setPend(r?.data || { total: 0 }); })
            .catch(() => { if (ativo) setPend({ total: 0 }); });
        return () => { ativo = false; };
    }, [id_student, recarregar]);
    return pend;
}

/** Aviso no painel do aluno: ocorrência nova aguardando ciência. */
export function AvisoOcorrencias({ pend, onAbrir }) {
    if (!pend?.total) return null;
    const soElogio = pend.elogios === pend.total;
    return (
        <button type="button" onClick={onAbrir} style={{
            display: 'block', width: '100%', textAlign: 'left', cursor: 'pointer', margin: '12px 0',
            borderRadius: 12, padding: '12px 14px', fontSize: 14, lineHeight: 1.45,
            background: soElogio ? '#e3f4ec' : '#fff4e5', border: `1px solid ${soElogio ? '#16a34a' : '#f59e0b'}`,
            color: soElogio ? '#14532d' : '#7c4a03',
        }}>
            <strong>{soElogio ? '⭐' : '📝'} {pend.total === 1 ? 'Há 1 ocorrência nova' : `Há ${pend.total} ocorrências novas`}</strong>
            {pend.graves > 0 && <> — {pend.graves} grave{pend.graves > 1 ? 's' : ''}</>}
            <div style={{ fontSize: 12.5 }}>Toque para ver e marcar que está ciente.</div>
        </button>
    );
}

/** Lista das ocorrências (só leitura) com o botão "Estou ciente". */
export function ModalOcorrenciasPais({ open, onClose, id_student, nome, onMudou }) {
    const [dados, setDados] = useState(null);
    const [ano, setAno] = useState('');
    const [loading, setLoading] = useState(false);
    const [erro, setErro] = useState('');
    const [salvando, setSalvando] = useState('');

    const carregar = useCallback(async () => {
        if (!open || !id_student) return;
        setLoading(true);
        setErro('');
        try {
            const r = await api.post('/occurrence/student', { id_student, ...(ano ? { year: ano } : {}) });
            setDados(r.data);
            if (!ano && r.data?.year) setAno(String(r.data.year));
        } catch (e) {
            setErro(e?.response?.data?.msg || 'Não foi possível carregar as ocorrências.');
        } finally {
            setLoading(false);
        }
    }, [open, id_student, ano]);

    useEffect(() => { carregar(); }, [carregar]);

    const ciente = async (o) => {
        setSalvando(o._id);
        try {
            const r = await api.post('/occurrence/ack', { id: o._id });
            setDados((d) => ({ ...d, ocorrencias: d.ocorrencias.map((x) => (x._id === o._id ? { ...x, ciencia: r.data.ciencia } : x)) }));
            if (onMudou) onMudou();
        } catch (e) {
            setErro(e?.response?.data?.msg || 'Não foi possível registrar a ciência.');
        } finally {
            setSalvando('');
        }
    };

    if (!open) return null;
    const lista = dados?.ocorrencias || [];

    return (
        <div onClick={onClose} style={{
            position: 'fixed', inset: 0, background: 'rgba(15, 30, 40, .5)', backdropFilter: 'blur(4px)', WebkitBackdropFilter: 'blur(4px)',
            zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 12,
        }}>
            <div onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true" aria-label="Ocorrências" style={{
                background: 'var(--surface, #fff)', color: 'var(--text, #16323b)', borderRadius: 14, width: '100%', maxWidth: 680,
                maxHeight: '92vh', display: 'flex', flexDirection: 'column', overflow: 'hidden', boxShadow: '0 20px 50px rgba(0,0,0,.25)',
            }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '14px 16px', borderBottom: '1px solid var(--line, #e3e8eb)' }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontWeight: 800, fontSize: 17 }}>📝 Ocorrências</div>
                        {nome && <div style={{ fontSize: 13, color: 'var(--text-muted, #5b6b72)' }}>{nome}</div>}
                    </div>
                    {(dados?.anos || []).length > 1 && (
                        <select value={ano} onChange={(e) => setAno(e.target.value)} aria-label="Ano letivo" style={{
                            padding: '6px 10px', borderRadius: 8, border: '1px solid var(--line, #d0d7db)', background: 'var(--surface, #fff)', color: 'var(--text, #16323b)',
                        }}>
                            {dados.anos.map((y) => <option key={y} value={y}>{y}</option>)}
                        </select>
                    )}
                    <button type="button" onClick={onClose} aria-label="Fechar" style={{ border: 'none', background: 'transparent', fontSize: 22, cursor: 'pointer', color: 'var(--text, #16323b)' }}>×</button>
                </div>
                <div style={{ overflowY: 'auto', padding: 16, display: 'flex', flexDirection: 'column', gap: 10 }}>
                    {loading && <div style={{ textAlign: 'center', padding: 24 }}>Carregando…</div>}
                    {!loading && erro && <div style={{ color: '#dc2626' }}>{erro}</div>}
                    {!loading && !erro && lista.length === 0 && (
                        <div style={{ textAlign: 'center', padding: 24, color: 'var(--text-muted, #5b6b72)' }}>Nenhuma ocorrência registrada neste ano. 🎉</div>
                    )}
                    {!loading && lista.map((o) => (
                        <CartaoOcorrencia key={o._id} o={o} paraPais onCiente={ciente} cienteCarregando={salvando === o._id} />
                    ))}
                </div>
            </div>
        </div>
    );
}
